"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CASE_DEPOSIT_INLINE_ERROR = exports.CASE_DEPOSIT_EXCEEDS_MESSAGE = void 0;
exports.isContractDepositPaymentTerm = isContractDepositPaymentTerm;
exports.extractCaseDepositPercent = extractCaseDepositPercent;
exports.findContractDepositMilestoneWithReason = findContractDepositMilestoneWithReason;
exports.findContractDepositMilestone = findContractDepositMilestone;
exports.describeCaseExtractionProcessing = describeCaseExtractionProcessing;
exports.validateCaseExtractedDepositPercent = validateCaseExtractedDepositPercent;
exports.prepareCaseExtractedMilestones = prepareCaseExtractedMilestones;
const case_workflow_constants_js_1 = require("./case-workflow.constants.js");
const case_deposit_helpers_js_1 = require("./case-deposit.helpers.js");
const case_workflow_helpers_js_1 = require("./case-workflow.helpers.js");
exports.CASE_DEPOSIT_EXCEEDS_MESSAGE = `Your deposit exceeds ${case_workflow_constants_js_1.CASE_DEPOSIT_MAX_PERCENT}%, please reduce it to ${case_workflow_constants_js_1.CASE_DEPOSIT_MAX_PERCENT}% or less and try again.`;
exports.CASE_DEPOSIT_INLINE_ERROR = `Deposit cannot exceed ${case_workflow_constants_js_1.CASE_DEPOSIT_MAX_PERCENT}% of project budget`;
/**
 * Soft hints only — used for debug/logging. Detection itself is by position (first payment event).
 * Kept so support can see when the first row also matched familiar deposit wording.
 */
const DEPOSIT_KEYWORDS = [
    "deposit",
    "confirmation",
    "booking",
    "retainer",
    "upon signing",
    "upon contract",
    "contract signing",
    "advance",
    "initial payment",
    "upfront",
    "down payment",
];
function milestoneText(milestone) {
    return `${milestone.name ?? ""} ${milestone.description ?? ""}`.toLowerCase();
}
/** True when a payment row looks deposit-like by wording (debug / soft hint only). */
function isContractDepositPaymentTerm(milestone) {
    if ((0, case_workflow_helpers_js_1.isCaseDepositMilestone)({ name: milestone.name, metadata: milestone.metadata })) {
        return true;
    }
    const text = milestoneText(milestone);
    if (DEPOSIT_KEYWORDS.some((keyword) => text.includes(keyword))) {
        return true;
    }
    return false;
}
function extractCaseDepositPercent(milestone, contractValue) {
    const fromMetadata = milestone.metadata?.["budgetPercent"];
    if (typeof fromMetadata === "number" && !Number.isNaN(fromMetadata)) {
        return fromMetadata;
    }
    if (typeof milestone.budgetPercent === "number" && !Number.isNaN(milestone.budgetPercent)) {
        return milestone.budgetPercent;
    }
    if (contractValue > 0 &&
        typeof milestone.budgetAmount === "number" &&
        !Number.isNaN(milestone.budgetAmount)) {
        return Math.round((milestone.budgetAmount / contractValue) * 10000) / 100;
    }
    return null;
}
function resolveMilestonePercent(milestone, contractValue) {
    const pct = extractCaseDepositPercent(milestone, contractValue);
    return pct != null && !Number.isNaN(pct) ? pct : 0;
}
function defaultDate(value, fallback) {
    if (value && value.trim())
        return value;
    if (fallback && fallback.trim())
        return fallback;
    return new Date().toISOString();
}
function findMatchingDepositKeyword(milestone) {
    const text = milestoneText(milestone);
    return DEPOSIT_KEYWORDS.find((keyword) => text.includes(keyword));
}
/**
 * CASE deposit = the first payment event in document order.
 * Display name stays whatever the contract/AI used (Confirmation, Booking, Deposit, …).
 * Keyword match is recorded only for debug — it does not choose which row is the deposit.
 */
function findContractDepositMilestoneWithReason(milestones) {
    if (!milestones.length) {
        return { milestone: null, reason: "none" };
    }
    const first = milestones[0];
    if ((0, case_workflow_helpers_js_1.isCaseDepositMilestone)({ name: first.name, metadata: first.metadata })) {
        return {
            milestone: first,
            reason: "case-deposit-metadata",
            matchedKeyword: findMatchingDepositKeyword(first),
        };
    }
    return {
        milestone: first,
        reason: "first-payment",
        matchedKeyword: findMatchingDepositKeyword(first),
    };
}
function findContractDepositMilestone(milestones) {
    return findContractDepositMilestoneWithReason(milestones).milestone;
}
function describeCaseExtractionProcessing(input) {
    const contractValue = input.contractValue > 0 ? input.contractValue : 0;
    const extracted = input.extractedMilestones ?? [];
    const inputMilestones = extracted.map((milestone) => ({
        name: milestone.name ?? null,
        description: milestone.description ?? null,
        budgetPercent: typeof milestone.budgetPercent === "number"
            ? milestone.budgetPercent
            : typeof milestone.metadata?.["budgetPercent"] === "number"
                ? milestone.metadata["budgetPercent"]
                : null,
        budgetAmount: typeof milestone.budgetAmount === "number" ? milestone.budgetAmount : null,
        derivedPercent: extractCaseDepositPercent(milestone, contractValue),
    }));
    const detection = findContractDepositMilestoneWithReason(extracted);
    const depositPercent = detection.milestone
        ? extractCaseDepositPercent(detection.milestone, contractValue)
        : null;
    const depositValidation = validateCaseExtractedDepositPercent(extracted, contractValue);
    const prepared = depositValidation.valid === true
        ? prepareCaseExtractedMilestones(input)
        : { valid: false, message: depositValidation.message };
    return {
        contractValue,
        inputMilestones,
        detectedDeposit: detection.milestone
            ? {
                name: detection.milestone.name ?? null,
                description: detection.milestone.description ?? null,
                reason: detection.reason,
                matchedKeyword: detection.matchedKeyword,
                depositPercent,
                maxAllowedPercent: case_workflow_constants_js_1.CASE_DEPOSIT_MAX_PERCENT,
            }
            : null,
        validation: {
            valid: depositValidation.valid === true,
            message: depositValidation.valid === false ? depositValidation.message : undefined,
        },
        strippedDeposit: detection.milestone != null && depositValidation.valid === true,
        outputMilestones: prepared.valid === true
            ? prepared.milestones.map((milestone) => ({
                name: milestone.name,
                budgetPercent: milestone.budgetPercent,
                budgetAmount: milestone.budgetAmount,
            }))
            : [],
    };
}
function validateCaseExtractedDepositPercent(milestones, contractValue) {
    const deposit = findContractDepositMilestone(milestones);
    if (!deposit)
        return { valid: true };
    const percent = extractCaseDepositPercent(deposit, contractValue);
    if (percent != null && percent > case_workflow_constants_js_1.CASE_DEPOSIT_MAX_PERCENT) {
        return { valid: false, message: exports.CASE_DEPOSIT_EXCEEDS_MESSAGE };
    }
    return { valid: true };
}
function prepareCaseExtractedMilestones(input) {
    const contractValue = input.contractValue > 0 ? input.contractValue : 0;
    const extracted = input.extractedMilestones ?? [];
    const depositValidation = validateCaseExtractedDepositPercent(extracted, contractValue);
    if (depositValidation.valid === false) {
        return { valid: false, message: depositValidation.message };
    }
    const deposit = findContractDepositMilestone(extracted);
    const nonDeposit = extracted.filter((m) => m !== deposit);
    const milestones = nonDeposit.map((milestone) => {
        const budgetPercent = resolveMilestonePercent(milestone, contractValue);
        const budgetAmount = contractValue > 0
            ? (0, case_deposit_helpers_js_1.computeCaseBudgetAmount)(contractValue, budgetPercent)
            : Number(milestone.budgetAmount) || 0;
        return {
            name: milestone.name?.trim() || case_workflow_constants_js_1.CASE_DEPOSIT_MILESTONE_NAME,
            description: milestone.description ?? null,
            budgetAmount,
            budgetPercent,
            startDate: defaultDate(milestone.startDate, input.projectStartDate),
            endDate: defaultDate(milestone.endDate, input.projectEndDate),
            metadata: { budgetPercent, usePercentageMode: true },
        };
    });
    return { valid: true, milestones };
}
